import { supabase } from './supabase'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB in bytes
const MAX_VIDEO_SIZE = 50 * 1024 * 1024 // 50MB in bytes
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo']

export interface UploadImageResult {
  url: string | null
  error: string | null
}

/**
 * Uploads an image file to Supabase Storage
 * @param file - The image file to upload
 * @param folder - The folder path in the storage bucket (e.g., 'tours', 'hotels')
 * @returns Promise with the public URL or error message
 */
export async function uploadImage(
  file: File,
  folder: string = 'images'
): Promise<UploadImageResult> {
  // Validate file size (2MB limit)
  if (file.size > MAX_FILE_SIZE) {
    return {
      url: null,
      error: `File size exceeds 2MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    }
  }

  // Validate file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      url: null,
      error: `Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`
    }
  }

  try {
    // Generate unique filename
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`
    const filePath = `${folder}/${fileName}`

    // Upload file to Supabase Storage
    const { error: uploadError, data } = await supabase.storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) {
      // Provide helpful error message for missing bucket
      if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('not found')) {
        return {
          url: null,
          error: 'Storage bucket "images" not found. Please create it in Supabase Dashboard → Storage → New Bucket. Name it "images" and set it to Public.'
        }
      }
      return {
        url: null,
        error: uploadError.message || 'Failed to upload image'
      }
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(filePath)

    return {
      url: urlData.publicUrl,
      error: null
    }
  } catch (error) {
    return {
      url: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

/**
 * Uploads multiple image files to Supabase Storage
 * @param files - Array of image files to upload
 * @param folder - The folder path in the storage bucket (e.g., 'tours', 'hotels')
 * @returns Promise with array of results (urls and errors)
 */
export async function uploadMultipleImages(
  files: File[],
  folder: string = 'images'
): Promise<{ urls: string[]; errors: string[] }> {
  const urls: string[] = []
  const errors: string[] = []

  // Upload all files in parallel
  const uploadPromises = files.map(file => uploadImage(file, folder))
  const results = await Promise.all(uploadPromises)

  // Collect results
  results.forEach((result, index) => {
    if (result.url) {
      urls.push(result.url)
    } else if (result.error) {
      errors.push(`File ${index + 1}: ${result.error}`)
    }
  })

  return { urls, errors }
}

/**
 * Uploads a video file to Supabase Storage
 * @param file - The video file to upload
 * @param folder - The folder path in the storage bucket (e.g., 'testimonials', 'tours')
 * @returns Promise with the public URL or error message
 */
export async function uploadVideo(
  file: File,
  folder: string = 'videos'
): Promise<UploadImageResult> {
  // Validate file size (50MB limit)
  if (file.size > MAX_VIDEO_SIZE) {
    return {
      url: null,
      error: `File size exceeds 50MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    }
  }

  // Validate file type
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return {
      url: null,
      error: `Invalid file type. Allowed types: ${ALLOWED_VIDEO_TYPES.join(', ')}`
    }
  }

  try {
    // Generate unique filename
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`
    const filePath = `${folder}/${fileName}`

    // Upload file to Supabase Storage
    const { error: uploadError, data } = await supabase.storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) {
      // Provide helpful error message for missing bucket
      if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('not found')) {
        return {
          url: null,
          error: 'Storage bucket "images" not found. Please create it in Supabase Dashboard → Storage → New Bucket. Name it "images" and set it to Public.'
        }
      }
      return {
        url: null,
        error: uploadError.message || 'Failed to upload video'
      }
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(filePath)

    return {
      url: urlData.publicUrl,
      error: null
    }
  } catch (error) {
    return {
      url: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

/**
 * Deletes an image from Supabase Storage
 * @param url - The public URL of the image to delete
 * @returns Promise with success status or error message
 */
export async function deleteImage(url: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Extract file path from URL
    const urlParts = url.split('/')
    const filePath = urlParts.slice(urlParts.indexOf('images') + 1).join('/')

    const { error } = await supabase.storage
      .from('images')
      .remove([filePath])

    if (error) {
      return {
        success: false,
        error: error.message
      }
    }

    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

